/**
 * 🎨 AESTHETIC DIRECTION: Editorial Reading Experience
 * 
 * Inspired by: Medium, Substack, high-end digital magazines
 * 
 * FONTS:
 * - Headlines: Georgia (classic, serif, elegant)
 * - Body: -apple-system (optimal readability)
 * - Numbers: Monospace (technical precision)
 * 
 * COLORS:
 * - Primary: Warm blacks (#1a1a1a)
 * - Accent: Amber warm tones (#f59e0b)
 * - Background: Soft cream (#fafaf9)
 * - Highlights: Soft yellow (#fef3c7)
 * 
 * KEY STANDOUT IDEAS:
 * - Magazine-style two-column layout on desktop
 * - Generous line-height (1.8) for comfortable reading
 * - Serif headlines for authority
 * - Subtle animations on scroll
 * - Content-first design with minimal chrome
 * - Progress indicator showing reading position
 */

import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { FiCheckCircle, FiInfo, FiHelpCircle, FiBookOpen } from "react-icons/fi";
import "./GuidePage.css";

const GuidePage = ({
  title,
  description,
  videoUrl,
  steps = [],
  features = [],
  tips = [],
  faqs = [],
  example,
  tool,
}) => {
  const [readingProgress, setReadingProgress] = useState(0);
  const location = useLocation();
  const canonicalUrl = `https://autowork.com.vn${location.pathname}`;
  const seoTitle = `${title} | Hướng Dẫn AutoWork`;
  const seoDescription =
    description ||
    "Hướng dẫn chi tiết sử dụng AutoWork để tự động hóa viết và đăng bài cho fanpage.";

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const progress = (window.scrollY / totalHeight) * 100;
      setReadingProgress(progress);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="guide-page-wrapper">
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
      </Helmet>
      {/* Reading Progress Bar */}
      <div className="reading-progress-bar">
        <div 
          className="reading-progress-fill" 
          style={{ width: `${readingProgress}%` }}
        />
      </div>

      <article className="guide-article">
        {/* Hero Header */}
        <header className="guide-header">
          <div className="guide-label">
            <FiBookOpen className="guide-label-icon" />
            <span>Hướng dẫn sử dụng</span>
          </div>
          <h1 className="guide-title">{title}</h1>
          <p className="guide-description">{description}</p>
        </header>

        {/* Video Tutorial */}
        {videoUrl && (
          <section className="guide-section video-section">
            <div className="video-wrapper">
              <iframe
                src={videoUrl}
                title={`Tutorial: ${title}`}
                className="video-iframe"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </section>
        )}

        {/* Step by Step Instructions */}
        {steps.length > 0 && (
          <section className="guide-section">
            <h2 className="section-title">
              <span className="section-number">01</span>
              Hướng dẫn từng bước
            </h2>
            
            <div className="guide-steps-container">
              {steps.map((step, index) => (
                <div key={index} className="guide-step-item">
                  <div className="guide-step-marker">
                    <span className="guide-step-number">{String(index + 1).padStart(2, '0')}</span>
                  </div>
                  <div className="guide-step-content">
                    <h3 className="guide-step-title">{step.title}</h3>
                    <div className="guide-step-text">
                      {step.content.split('\n').map((line, i) => (
                        <p key={i}>{line}</p>
                      ))}
                    </div>
                    {step.image && (
                      <div className="guide-step-image">
                        <img src={step.image} alt={step.title} />
                      </div>
                    )}
                    {step.note && (
                      <div className="guide-step-note">
                        <FiInfo className="note-icon" />
                        <div className="note-content">{step.note}</div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Key Features */}
        {features.length > 0 && (
          <section className="guide-section features-section">
            <h2 className="section-title">
              <span className="section-number">02</span>
              Tính năng nổi bật
            </h2>
            
            <div className="features-grid">
              {features.map((feature, index) => (
                <div key={index} className="feature-card">
                  <div className="feature-icon">
                    <FiCheckCircle />
                  </div>
                  <h3 className="feature-name">{feature.name}</h3>
                  <p className="feature-description">{feature.description}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Tips */}
        {tips.length > 0 && (
          <section className="guide-section tips-section">
            <div className="tips-card">
              <h2 className="section-title tips-title">
                💡 Mẹo hữu ích
              </h2>
              <ul className="tips-list">
                {tips.map((tip, index) => (
                  <li key={index} className="tip-item">
                    <span className="tip-bullet">→</span>
                    <span className="tip-text">{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {/* Real-world Example */}
        {example && (
          <section className="guide-section example-section">
            <h2 className="section-title">
              <span className="section-number">03</span>
              Ví dụ thực tế
            </h2>
            
            <div className="example-card">
              <div className="example-scenario">
                <span className="example-quote">"</span>
                {example.scenario}
              </div>
              
              <div className="example-details">
                <div className="example-item">
                  <div className="example-label">Đầu vào</div>
                  <div className="example-value">{example.input}</div>
                </div>
                
                <div className="example-item">
                  <div className="example-label">Cấu hình</div>
                  <div className="example-value">{example.config}</div>
                </div>
                
                <div className="example-item">
                  <div className="example-label">Kết quả</div>
                  <div className="example-value">{example.result}</div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Tool Integration */}
        {tool && (
          <section className="guide-section tool-section">
            <h2 className="section-title">
              <span className="section-number">{String((steps.length > 0 ? 1 : 0) + (features.length > 0 ? 1 : 0) + (example ? 1 : 0) + (faqs.length > 0 ? 1 : 0) + 1).padStart(2, '0')}</span>
              Trải nghiệm ngay
            </h2>
            <div className="tool-container">
              {tool}
            </div>
          </section>
        )}

        {/* FAQs */}
        {faqs.length > 0 && (
          <section className="guide-section faqs-section">
            <h2 className="section-title">
              <span className="section-number">{String((steps.length > 0 ? 1 : 0) + (features.length > 0 ? 1 : 0) + (example ? 1 : 0) + (tool ? 1 : 0) + 1).padStart(2, '0')}</span>
              Câu hỏi thường gặp
            </h2>
            
            <div className="faqs-container">
              {faqs.map((faq, index) => (
                <details key={index} className="faq-item">
                  <summary className="faq-question">
                    <FiHelpCircle className="faq-icon" />
                    <span>{faq.question}</span>
                    <span className="faq-arrow">→</span>
                  </summary>
                  <div className="faq-answer">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </section>
        )}
      </article>
    </div>
  );
};

export default GuidePage;
